/**
 * 系统设置页 — SystemSettings（二期批3 翻新，对齐概念稿 system-settings.html）。
 *
 * GET /api/system/monitor-platforms   监测平台清单（动态生成，失败兜底默认 5 平台）
 * GET /api/system/settings/llm-keys   LLM API Key 配置（脱敏）
 * PUT /api/system/settings/llm-keys   单平台 Key / Endpoint / Model 更新
 *
 * 结构：监测平台清单卡 → LLM API Key 配置卡组（按平台）→ 配置说明卡。
 * 仅 admin 可访问（菜单批1已过滤；后端 403 兜底，本页展示错误态）。
 *
 * 概念稿裁剪：预警阈值配置 / 收录复检配置 / 数据治理三卡为概念稿只读示意，
 * 无后端配置端点，按"接口没有不虚构"原则跳过；监测平台清单与 LLM Key 为真实配置。
 */
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Save,
  CheckCircle,
  VpnKey,
} from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import Badge from "../components/Badge";
import {
  getLlmKeys,
  updateLlmKeys,
  getMonitorPlatforms,
  getSettings,
  updateSettings,
  uploadLeadgenQr,
  type PlatformConfig,
  type MonitorPlatform,
} from "../api/system";
import { theme } from "../theme/theme";
import { PLATFORM_COLORS } from "../utils/constants";

/** 平台中文名映射（monitor-platforms 未返回 label 时的兜底）。 */
const PLATFORM_NAMES: Record<string, string> = {
  doubao: "豆包（火山引擎）",
  deepseek: "DeepSeek",
  qwen: "千问（通义千问）",
  yuanbao: "元宝（腾讯混元）",
  wenxin: "文心一言（百度千帆）",
};

/** 监测平台接口加载失败时的兜底清单。 */
const DEFAULT_MONITOR_PLATFORMS: MonitorPlatform[] = [
  { key: "doubao", label: "豆包", enabled: true },
  { key: "deepseek", label: "DeepSeek", enabled: true },
  { key: "qwen", label: "通义千问", enabled: true },
  { key: "yuanbao", label: "元宝", enabled: true },
  { key: "wenxin", label: "文心一言", enabled: true },
];

/** 引流报告联系区块二维码槽位（kind → 文案）。 */
const LEADGEN_QR_SLOTS = [
  { kind: "group" as const, key: "LEADGEN_QR_GROUP", label: "微信群二维码", desc: "报告联系区块左侧" },
  { kind: "sales" as const, key: "LEADGEN_QR_SALES", label: "销售二维码", desc: "报告联系区块右侧" },
];

/**
 * 引流报告联系区块配置卡（批 10 任务三）。
 * 双二维码上传（png/jpg ≤2MB，上传后预览）+ 联系电话；
 * 配置存 system_settings（category=leadgen），报告页公开读取。
 */
function LeadgenContactCard({
  notify,
}: {
  notify: (message: string, severity: "success" | "error") => void;
}) {
  const [phone, setPhone] = useState("");
  const [qrUrls, setQrUrls] = useState<Record<string, string>>({});
  const [savingPhone, setSavingPhone] = useState(false);
  const [uploading, setUploading] = useState<string>("");

  const load = useCallback(async () => {
    try {
      const items = await getSettings("leadgen");
      const map: Record<string, string> = {};
      let phoneVal = "";
      for (const it of items) {
        if (it.key === "LEADGEN_PHONE") phoneVal = it.value || "";
        if (it.has_value) map[it.key] = it.value;
      }
      setPhone(phoneVal);
      setQrUrls(map);
    } catch (e: any) {
      notify(e.message || "联系区块配置加载失败", "error");
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSavePhone = async () => {
    setSavingPhone(true);
    try {
      await updateSettings([{ key: "LEADGEN_PHONE", value: phone.trim() }]);
      notify("联系电话已保存", "success");
      await load();
    } catch (e: any) {
      notify(e.message || "保存失败", "error");
    } finally {
      setSavingPhone(false);
    }
  };

  const handleUpload = async (kind: "group" | "sales", file: File | null) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      notify("仅支持 png/jpg 图片", "error");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      notify("图片大小不能超过 2MB", "error");
      return;
    }
    setUploading(kind);
    try {
      await uploadLeadgenQr(kind, file);
      notify("二维码已上传并生效", "success");
      await load();
    } catch (e: any) {
      notify(e.message || "上传失败", "error");
    } finally {
      setUploading("");
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            引流报告联系区块
          </Typography>
          <Badge label="官网免费诊断报告页" variant="info" size="medium" />
        </Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
          官网免费诊断报告结尾的联系区块：双二维码并排 + 联系电话 + 预约完整诊断按钮。
          未上传二维码时报告只显示电话和按钮，不出现空图。
        </Typography>

        {/* 双二维码上传 + 预览 */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2.5 }}>
          {LEADGEN_QR_SLOTS.map((slot) => {
            const url = qrUrls[slot.key];
            return (
              <Box
                key={slot.kind}
                sx={{
                  border: "1px solid", borderColor: "divider", borderRadius: 2,
                  p: 2, width: 220, textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                  {slot.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1.5 }}>
                  {slot.desc} · png/jpg ≤ 2MB
                </Typography>
                {url ? (
                  <Box
                    component="img"
                    src={`${url}?v=${Date.now()}`}
                    alt={slot.label}
                    sx={{
                      width: 120, height: 120, borderRadius: 1,
                      border: "1px solid", borderColor: "divider",
                      objectFit: "cover", mb: 1.5, bgcolor: "#fff",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 120, height: 120, borderRadius: 1, mb: 1.5, mx: "auto",
                      border: "1px dashed", borderColor: "divider",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, color: "text.disabled",
                    }}
                  >
                    未上传
                  </Box>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  disabled={uploading === slot.kind}
                  sx={{ fontSize: 12, textTransform: "none" }}
                >
                  {uploading === slot.kind ? "上传中..." : url ? "更换图片" : "上传图片"}
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => {
                      handleUpload(slot.kind, e.target.files?.[0] ?? null);
                      e.target.value = "";
                    }}
                  />
                </Button>
              </Box>
            );
          })}
        </Box>

        {/* 联系电话 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TextField
            label="联系电话"
            size="small"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="如 400-800-1234"
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": { fontSize: 13 },
              "& .MuiInputLabel-root": { fontSize: 13 },
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Save />}
            onClick={handleSavePhone}
            disabled={savingPhone}
            sx={{ fontSize: 13, textTransform: "none" }}
          >
            {savingPhone ? "保存中..." : "保存电话"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/** 单个平台配置卡片。 */
function PlatformCard({
  config,
  displayName,
  onSave,
}: {
  config: PlatformConfig;
  /** 平台显示名（来自 monitor-platforms 清单）。 */
  displayName?: string;
  onSave: (
    platform: string,
    data: { api_key?: string; endpoint?: string; model?: string },
  ) => Promise<void>;
}) {
  const platform = config.platform;
  const platformName = displayName || PLATFORM_NAMES[platform] || platform;
  const color = PLATFORM_COLORS[platform] || theme.palette.primary.main;

  // 表单状态
  const [apiKey, setApiKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [model, setModel] = useState("");

  // 显示密码
  const [showKey, setShowKey] = useState(false);

  // 保存状态
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 是否有已配置的 Key
  const hasKey = config.api_key?.has_value ?? false;

  useEffect(() => {
    // 初始化：脱敏值不回填到输入框（用户需要重新输入才修改）
    setApiKey("");
    setEndpoint(config.endpoint?.value || "");
    setModel(config.model?.value || "");
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data: { api_key?: string; endpoint?: string; model?: string } = {};
      // api_key 为空字符串不提交（不改Key）
      // 只有用户输入了内容才提交
      if (apiKey.trim()) data.api_key = apiKey.trim();
      if (endpoint.trim()) data.endpoint = endpoint.trim();
      if (model.trim()) data.model = model.trim();

      if (Object.keys(data).length === 0) {
        setSaving(false);
        return;
      }

      await onSave(platform, data);
      setSaved(true);
      setApiKey(""); // 保存后清空密码框
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // 错误由上层 Snackbar 处理
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      sx={{
        border: "1px solid", borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* 平台标题栏 */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: `${color}08`,
          borderBottom: "1px solid", borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <VpnKey sx={{ fontSize: 18, color }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary" }}>
          {platformName}
        </Typography>
        {hasKey ? (
          <Chip
            label="已配置"
            size="small"
            sx={{
              fontSize: 11,
              height: 20,
              bgcolor: "semanticBg.success",
              color: "success.main",
            }}
          />
        ) : (
          <Chip
            label="未配置"
            size="small"
            sx={{
              fontSize: 11,
              height: 20,
              bgcolor: "semanticBg.error",
              color: "error.main",
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* API Key */}
        <TextField
          label="API Key"
          size="small"
          fullWidth
          type={showKey ? "text" : "password"}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={hasKey ? `已配置（${config.api_key?.value}），输入新值覆盖` : "请输入 API Key"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowKey(!showKey)}
                  edge="end"
                >
                  {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": { fontSize: 13 },
            "& .MuiInputLabel-root": { fontSize: 13 },
          }}
        />

        {/* Endpoint */}
        <TextField
          label="Endpoint"
          size="small"
          fullWidth
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          placeholder="API 端点地址"
          sx={{
            "& .MuiOutlinedInput-root": { fontSize: 13 },
            "& .MuiInputLabel-root": { fontSize: 13 },
          }}
        />

        {/* Model */}
        <TextField
          label="Model"
          size="small"
          fullWidth
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="模型名称"
          sx={{
            "& .MuiOutlinedInput-root": { fontSize: 13 },
            "& .MuiInputLabel-root": { fontSize: 13 },
          }}
        />

        {/* 保存按钮 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saved ? <CheckCircle /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: color,
              fontSize: 13,
              textTransform: "none",
              "&:hover": { bgcolor: color, opacity: 0.9 },
            }}
          >
            {saving ? "保存中..." : saved ? "已保存" : "保存"}
          </Button>
          {config.api_key?.updated_at && (
            <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
              上次更新: {config.api_key.updated_by} ·{" "}
              {new Date(config.api_key.updated_at).toLocaleString("zh-CN")}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SystemSettings() {
  const [platforms, setPlatforms] = useState<PlatformConfig[]>([]);
  const [monitorPlatforms, setMonitorPlatforms] = useState<MonitorPlatform[]>(
    DEFAULT_MONITOR_PLATFORMS,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  /** 加载 LLM Key 配置。 */
  const fetchLlmKeys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLlmKeys();
      setPlatforms(data.platforms);
    } catch (e: any) {
      setError(e.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLlmKeys();
  }, [fetchLlmKeys]);

  /** 加载监测平台清单（失败时兜底默认 5 平台）。 */
  useEffect(() => {
    getMonitorPlatforms()
      .then((list) => {
        if (list.length > 0) setMonitorPlatforms(list);
      })
      .catch(() => setMonitorPlatforms(DEFAULT_MONITOR_PLATFORMS));
  }, []);

  /** 平台展示区：按监测平台清单渲染，合并对应 LLM Key 配置。 */
  const displayPlatforms = monitorPlatforms
    .filter((p) => p.enabled)
    .map((p) => ({
      platform: p.key,
      label: p.label || PLATFORM_NAMES[p.key] || p.key,
      config: platforms.find((c) => c.platform === p.key) ?? {
        platform: p.key,
      },
    }))
    // 兜底：llm-keys 返回但清单外的历史平台一并展示，避免配置入口丢失
    .concat(
      platforms
        .filter((c) => !monitorPlatforms.some((p) => p.key === c.platform))
        .map((c) => ({
          platform: c.platform,
          label: PLATFORM_NAMES[c.platform] || c.platform,
          config: c,
        })),
    );

  /** 副标题平台名串（启用中的平台）。 */
  const enabledNames = monitorPlatforms
    .filter((p) => p.enabled)
    .map((p) => p.label || PLATFORM_NAMES[p.key] || p.key);

  /** 已配置 Key 的平台数。 */
  const configuredCount = monitorPlatforms
    .filter((p) => p.enabled)
    .filter((p) => platforms.find((c) => c.platform === p.key)?.api_key?.has_value)
    .length;

  /** 稳定 notify（LeadgenContactCard 依赖，避免每次渲染重建触发循环加载）。 */
  const notify = useCallback(
    (message: string, severity: "success" | "error") =>
      setSnackbar({ open: true, message, severity }),
    [],
  );

  /** 保存单个平台配置。 */
  const handleSave = async (
    platform: string,
    data: { api_key?: string; endpoint?: string; model?: string },
  ) => {    try {
      await updateLlmKeys(platform, data);
      setSnackbar({
        open: true,
        message: `${PLATFORM_NAMES[platform] || platform} 配置已保存`,
        severity: "success",
      });
      // 重新加载配置（更新脱敏值和时间戳）
      await fetchLlmKeys();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.message || "保存失败",
        severity: "error",
      });
      throw e;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800 }}>
        <PageHeader
          title="系统设置"
          breadcrumbs={[{ label: "系统" }, { label: "系统设置" }]}
        />
        <Alert severity="error" sx={{ fontSize: 13 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <PageHeader
        title="系统设置"
        breadcrumbs={[{ label: "系统" }, { label: "系统设置" }]}
        subtitle={`监测平台清单 · LLM 网关 API Key 配置 — ${enabledNames.join(" / ")}`}
        actions={<Badge label="仅管理员" variant="info" size="medium" />}
      />

      {/* 监测平台清单（对齐概念稿第一卡） */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              监测平台清单
            </Typography>
            <Badge
              label={`${monitorPlatforms.filter((p) => p.enabled).length} 平台已接入`}
              variant="success"
            />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {monitorPlatforms.map((p) => {
              const label = p.label || PLATFORM_NAMES[p.key] || p.key;
              return p.enabled ? (
                <Badge key={p.key} label={`${label} · 已接入`} variant="success" />
              ) : (
                <Badge key={p.key} label={`${label} · 未启用`} />
              );
            })}
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
            平台清单由后端配置动态生成（system_setting · monitor_platforms），新增平台
            无需改前端；五平台在全站页面共用同一份清单。已配置 API Key{" "}
            {configuredCount} / {monitorPlatforms.filter((p) => p.enabled).length} 平台。
          </Typography>
        </CardContent>
      </Card>

      {/* LLM API Key 配置区域 */}
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: "text.primary", mb: 2 }}>
        LLM API Key 配置
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {displayPlatforms.map((item) => (
          <PlatformCard
            key={item.platform}
            config={item.config}
            displayName={item.label}
            onSave={handleSave}
          />
        ))}
        {displayPlatforms.length === 0 && (
          <Alert severity="info" sx={{ fontSize: 13 }}>
            暂无配置项，请先在后端执行数据库迁移。
          </Alert>
        )}
      </Box>

      {/* 引流报告联系区块配置（批 10 任务三） */}
      <LeadgenContactCard notify={notify} />

      {/* 说明 */}
      <Box sx={{ mt: 3, p: 2, bgcolor: "background.default", borderRadius: 2 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
          • API Key 在数据库存储，前端脱敏显示（仅末4位可见）
          <br />
          • 保存后立即生效，LLM 网关下次调用自动读取新配置
          <br />
          • Endpoint 和 Model 有默认值，仅 API Key 为必填项
          <br />• 预警阈值 / 收录复检 / 数据治理口径由后端统一管理，本站暂不开放配置
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
